import { dateTimeInputPartsToUtcMilli, utcMilliToDateTimeInputParts } from "../../../utils/cmpTimeZones";
import {
  CmpManageNewsletterRowReturn,
  CmpOutboundEmailFieldsFragment,
  CmpOutboundEmailStatusEnum,
  CmpNewsletterDataInput,
  CmpNewsletterFieldsFragment,
  CmpNewsletterNewsroomStatusEnum,
  CmpNewsletterRecipientGroupFieldsFragment,
  CmpNewsletterRecipientSelectionInput,
  CmpNewsletterRecipientSourceKindEnum,
  CmpNewsletterSenderReadinessStatusEnum,
  CmpNewsletterStatusEnum,
  CmpNewsletterTypeEnum,
  PreviewCmpNewsletterRecipientsQuery,
} from "../../../graphql/types";
import {
  EmailFooterProfile,
  EmailTemplateKey,
  getEmailTemplatePreset,
  parseEmailBuilderStateJson,
  renderEmailBuilderState,
  sanitizeEmailBuilderStateLinks,
  serializeEmailBuilderState,
} from "./emailBuilderData";
import {
  EmailCampaign,
  EmailDeliverySummary,
  EmailFormState,
  EmailOutboundRecord,
  EmailOutboundStatus,
  EmailStatus,
  EmailTemplateSelection,
  EmailType,
  NewsroomStatus,
  RecipientGroup,
  RecipientPreviewSummary,
  RecipientSourceId,
  buildDefaultCreateEmailForm,
  cloneEmailForm,
} from "./emailMockData";
import {
  cmpLongDateFormatter,
  cmpLongDateTimeFormatter,
} from "../../common/dashboard/formatters";
import { getPreserveExpectedErrorsForCallerContext } from "../../../utils/apolloErrorPolicy";
import { UserFacingError } from "../../../utils/userFacingError";

export const EMAIL_OPERATION_ERROR_CONTEXT =
  getPreserveExpectedErrorsForCallerContext({
    expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
  });

/**
 * Shown when an email action starts before the signed-in chamber has loaded.
 * Screens that know the organisation noun pass it through
 * applyCmpOrganizationNoun.
 */
export const EMAIL_CHAMBER_LOADING_MESSAGE =
  "Your chamber details are still loading. Wait a moment, then try again. If it keeps happening, refresh the page.";

/**
 * Text for a list or setting that did not load on an email screen. The
 * screen shows a "Try again" button next to it. `thing` example:
 * "your member list".
 */
export const emailLoadErrorText = (thing: string): string =>
  `We could not load ${thing}. Check your internet connection, then select Try again.`;

type ManageNewsletterRow = Pick<
  CmpManageNewsletterRowReturn,
  | "id"
  | "slug"
  | "subject"
  | "emailType"
  | "status"
  | "newsroomStatus"
  | "scheduledAtUtcMilli"
  | "sentAtUtcMilli"
  | "publishedAtUtcMilli"
  | "updatedAtUtcMilli"
  | "estimatedRecipientCount"
  | "invalidRecipientCount"
  | "skippedRecipientCount"
  | "sentRecipientCount"
  | "failedRecipientCount"
  | "skippedDeliveryCount"
  | "attachmentCount"
  | "openCount"
  | "clickCount"
> &
  Partial<
    Pick<
      CmpManageNewsletterRowReturn,
      "campaignName" | "audienceLabel" | "tags"
    >
  >;

const dateFormatter = cmpLongDateFormatter;

const dateTimeFormatter = cmpLongDateTimeFormatter;

// Dates and times use the chamber time zone when it is known (live Oct 31
// 2026: Manage Emails showed the staff computer's time). Same format.
const inTimeZone = (formatter: Intl.DateTimeFormat, timeZone?: string | null) => {
  if (!timeZone) return formatter;
  const { locale, month, day, year, hour, minute } = formatter.resolvedOptions();
  return new Intl.DateTimeFormat(locale, { month, day, year, hour, minute, timeZone } as Intl.DateTimeFormatOptions);
};

const formatDate = (utcMilli?: number | null, timeZone?: string | null) =>
  utcMilli ? inTimeZone(dateFormatter, timeZone).format(new Date(utcMilli)) : undefined;

const formatDateTime = (utcMilli?: number | null, timeZone?: string | null) =>
  utcMilli ? inTimeZone(dateTimeFormatter, timeZone).format(new Date(utcMilli)) : undefined;

const formatDateTimeLabel = (utcMilli?: number | null, timeZone?: string | null) =>
  formatDateTime(utcMilli, timeZone) || "";

const toLocalDateTimeInputParts = (utcMilli?: number | null, timeZone?: string | null) =>
  utcMilli ? utcMilliToDateTimeInputParts(utcMilli, timeZone) : { date: "", time: "" };

const toUtcMilli = (date: string, time: string, timeZone?: string | null): number | null => {
  if (!date || !time || !timeZone) return null;
  const parsed = dateTimeInputPartsToUtcMilli(date, time, timeZone);
  if (!Number.isFinite(parsed)) return null;
  const roundTrip = utcMilliToDateTimeInputParts(parsed, timeZone);
  // A clock gap must not silently move the requested send time.
  return roundTrip.date === date && roundTrip.time === time ? parsed : null;
};

export const emailSchedulePartsToUtcMilli = toUtcMilli;

// The schedule date field keeps typed text that is not a date, so the form can
// show an error at the field instead of losing the text.
export const isValidEmailScheduleDate = (date: string): boolean =>
  !!date && Number.isFinite(dateTimeInputPartsToUtcMilli(date, "12:00", "UTC"));

type EmailScheduleParts = Pick<EmailFormState, "scheduledDate" | "scheduledTime" | "timeZone">;

// A date and time that do not make one real moment in the chamber time zone.
export const hasInvalidEmailSchedule = (email: EmailScheduleParts): boolean =>
  !!email.scheduledDate && !!email.scheduledTime &&
  toUtcMilli(email.scheduledDate, email.scheduledTime, email.timeZone) === null;

// The schedule is optional, so an invalid one must not block the draft save
// that comes before a test email. That save keeps the last saved schedule
// instead. Save Draft, Schedule and Send still reject the invalid schedule.
export const withLastSavedScheduleIfInvalid = (
  email: EmailFormState,
  lastSavedEmail: EmailScheduleParts
): EmailFormState => {
  // Without the chamber time zone no schedule can be checked, so keep the error.
  if (!email.timeZone || !hasInvalidEmailSchedule(email)) return email;
  const lastSaved = { ...email, scheduledDate: lastSavedEmail.scheduledDate, scheduledTime: lastSavedEmail.scheduledTime };
  return hasInvalidEmailSchedule(lastSaved) ? { ...email, scheduledDate: "", scheduledTime: "" } : lastSaved;
};

// After that save, the form keeps the typed schedule so the field error stays
// visible and the change is still unsaved.
export const withTypedScheduleIfInvalid = (
  savedEmail: EmailFormState,
  typedEmail: EmailScheduleParts
): EmailFormState =>
  hasInvalidEmailSchedule(typedEmail)
    ? { ...savedEmail, scheduledDate: typedEmail.scheduledDate, scheduledTime: typedEmail.scheduledTime }
    : savedEmail;

const splitCommaList = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const splitDirectRecipients = (value: string): string[] =>
  value
    .split(/[\s,;]+/)
    .map((recipient) => recipient.trim())
    .filter(Boolean);

const htmlEscape = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const textToHtml = (value: string): string =>
  value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map(
      (paragraph) => `<p>${htmlEscape(paragraph).replace(/\n/g, "<br />")}</p>`
    )
    .join("");

export const htmlToText = (value?: string | null): string =>
  (value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();

const formatFileSize = (sizeBytes: number): string => {
  if (sizeBytes < 1024 * 1024) {
    return `${Math.max(sizeBytes / 1024, 1).toFixed(0)} KB`;
  }

  return `${(sizeBytes / 1024 / 1024).toFixed(2)} MB`;
};

export const formatAttachmentFileSize = formatFileSize;

export const buildEmailDuplicateDraft = (
  email: EmailFormState
): EmailFormState => ({
  ...cloneEmailForm(email),
  id: undefined,
  status: "draft",
  subject: `${email.subject || "Untitled email"} copy`,
  campaignName: `${email.campaignName || email.subject || "Untitled email"} (Copy)`,
  scheduledDate: "",
  scheduledTime: "10:00",
  sentRecipientCount: 0,
  failedRecipientCount: 0,
  skippedDeliveryCount: 0,
  openCount: 0,
  clickCount: 0,
  outboundEmails: [],
  reviewNote: "",
  reviewHistory: [],
  deliverySummary: null,
  attachments: [],
  publishToNewsroom: false,
  newsroomStatus: "notPublished",
  newsroomSlug: "",
});

export const outboundStatusFromApi = (
  status: CmpOutboundEmailStatusEnum
): EmailOutboundStatus => {
  switch (status) {
    case CmpOutboundEmailStatusEnum.Sending:
      return "sending";
    case CmpOutboundEmailStatusEnum.Failed:
      return "failed";
    case CmpOutboundEmailStatusEnum.Skipped:
      return "skipped";
    case CmpOutboundEmailStatusEnum.DeliveryUnknown:
      return "deliveryUnknown";
    case CmpOutboundEmailStatusEnum.Sent:
      return "sent";
  }
};

// Delivery tables show these words, not the raw kind and status codes.
const OUTBOUND_KIND_LABELS: Partial<Record<string, string>> = {
  NEWSLETTER_CAMPAIGN: "Campaign email",
  NEWSLETTER_REVIEW: "Test email",
};

export const outboundKindLabel = (kind: string): string =>
  OUTBOUND_KIND_LABELS[kind] ||
  kind
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const OUTBOUND_STATUS_LABELS: Record<EmailOutboundStatus, string> = {
  sending: "Sending",
  sent: "Sent",
  failed: "Not delivered",
  skipped: "Not sent",
  deliveryUnknown: "Not confirmed",
};

export const outboundStatusLabel = (status: EmailOutboundStatus): string =>
  OUTBOUND_STATUS_LABELS[status];

const outboundEmailReturnToUi = (
  outboundEmail: CmpOutboundEmailFieldsFragment,
  timeZone?: string | null
): EmailOutboundRecord => ({
  id: outboundEmail.id,
  kind: outboundKindLabel(outboundEmail.kind),
  status: outboundStatusFromApi(outboundEmail.status),
  toEmail: outboundEmail.toEmail,
  intendedToEmail: outboundEmail.intendedToEmail || "",
  recipientOverrideUsed: outboundEmail.recipientOverrideUsed,
  subject: outboundEmail.subject,
  errorMessage: outboundEmail.errorMessage || "",
  attachmentCount: outboundEmail.attachmentCount,
  createdAt: formatDateTimeLabel(outboundEmail.createdAtUtcMilli, timeZone),
  sentAt: formatDateTimeLabel(outboundEmail.sentAtUtcMilli, timeZone),
  failedAt: formatDateTimeLabel(outboundEmail.failedAtUtcMilli, timeZone),
  openedAt: formatDateTimeLabel(outboundEmail.openedAtUtcMilli, timeZone),
  clickedAt: formatDateTimeLabel(outboundEmail.clickedAtUtcMilli, timeZone),
});

const deliverySummaryReturnToUi = (
  newsletter: CmpNewsletterFieldsFragment,
  timeZone?: string | null
): EmailDeliverySummary | null =>
  newsletter.deliverySummary
    ? {
        totalOutboundEmailCount:
          newsletter.deliverySummary.totalOutboundEmailCount,
        sentCount: newsletter.deliverySummary.sentCount,
        failedCount: newsletter.deliverySummary.failedCount,
        skippedCount: newsletter.deliverySummary.skippedCount,
        intendedRecipientCount:
          newsletter.deliverySummary.intendedRecipientCount,
        actualRecipientCount: newsletter.deliverySummary.actualRecipientCount,
        recipientOverrideUsed: newsletter.deliverySummary.recipientOverrideUsed,
        latestOutboundEmailAt: formatDateTimeLabel(
          newsletter.deliverySummary.latestOutboundEmailAtUtcMilli,
          timeZone
        ),
      }
    : null;

export const buildSlugFromSubject = (subject: string) =>
  subject
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);

export const emailTypeToApi = (emailType: EmailType): CmpNewsletterTypeEnum => {
  switch (emailType) {
    case "pressRelease":
      return CmpNewsletterTypeEnum.PressRelease;
    case "bulletin":
      return CmpNewsletterTypeEnum.Bulletin;
    case "eBlast":
      return CmpNewsletterTypeEnum.EBlast;
    case "announcement":
      return CmpNewsletterTypeEnum.Announcement;
    case "advocacyUpdate":
      return CmpNewsletterTypeEnum.AdvocacyUpdate;
    case "memberWelcome":
      return CmpNewsletterTypeEnum.MemberWelcome;
    case "fromTheDesk":
      return CmpNewsletterTypeEnum.FromTheDesk;
    case "customEmail":
      return CmpNewsletterTypeEnum.CustomEmail;
    case "newsletter":
    default:
      return CmpNewsletterTypeEnum.Newsletter;
  }
};

export const emailTypeFromApi = (
  emailType: CmpNewsletterTypeEnum
): EmailType => {
  switch (emailType) {
    case CmpNewsletterTypeEnum.PressRelease:
      return "pressRelease";
    case CmpNewsletterTypeEnum.Bulletin:
      return "bulletin";
    case CmpNewsletterTypeEnum.EBlast:
      return "eBlast";
    case CmpNewsletterTypeEnum.Announcement:
      return "announcement";
    case CmpNewsletterTypeEnum.AdvocacyUpdate:
      return "advocacyUpdate";
    case CmpNewsletterTypeEnum.MemberWelcome:
      return "memberWelcome";
    case CmpNewsletterTypeEnum.FromTheDesk:
      return "fromTheDesk";
    case CmpNewsletterTypeEnum.CustomEmail:
      return "customEmail";
    case CmpNewsletterTypeEnum.Newsletter:
    default:
      return "newsletter";
  }
};

export const statusToApi = (status: EmailStatus): CmpNewsletterStatusEnum => {
  switch (status) {
    case "review":
      return CmpNewsletterStatusEnum.ReadyForReview;
    case "scheduled":
      return CmpNewsletterStatusEnum.Scheduled;
    case "sending":
      return CmpNewsletterStatusEnum.Sending;
    case "sent":
      return CmpNewsletterStatusEnum.Sent;
    case "canceled":
      return CmpNewsletterStatusEnum.Canceled;
    case "failed":
      return CmpNewsletterStatusEnum.Failed;
    case "draft":
    default:
      return CmpNewsletterStatusEnum.Draft;
  }
};

export const statusFromApi = (status: CmpNewsletterStatusEnum): EmailStatus => {
  switch (status) {
    case CmpNewsletterStatusEnum.ReadyForReview:
      return "review";
    case CmpNewsletterStatusEnum.Scheduled:
      return "scheduled";
    case CmpNewsletterStatusEnum.Sending:
      return "sending";
    case CmpNewsletterStatusEnum.Sent:
      return "sent";
    case CmpNewsletterStatusEnum.Canceled:
      return "canceled";
    case CmpNewsletterStatusEnum.Failed:
      return "failed";
    case CmpNewsletterStatusEnum.Archived:
    case CmpNewsletterStatusEnum.Draft:
    default:
      return "draft";
  }
};

export const newsroomStatusFromApi = (
  status: CmpNewsletterNewsroomStatusEnum
): NewsroomStatus => {
  switch (status) {
    case CmpNewsletterNewsroomStatusEnum.Published:
      return "published";
    case CmpNewsletterNewsroomStatusEnum.Unpublished:
      return "unpublished";
    case CmpNewsletterNewsroomStatusEnum.NotPublished:
    default:
      return "notPublished";
  }
};

export const newsroomStatusToApi = (
  status: NewsroomStatus
): CmpNewsletterNewsroomStatusEnum => {
  switch (status) {
    case "published":
      return CmpNewsletterNewsroomStatusEnum.Published;
    case "unpublished":
      return CmpNewsletterNewsroomStatusEnum.Unpublished;
    case "notPublished":
    default:
      return CmpNewsletterNewsroomStatusEnum.NotPublished;
  }
};

const sourceKindByRecipientSourceId: Record<
  RecipientSourceId,
  CmpNewsletterRecipientSourceKindEnum
> = {
  activePrimaryContacts: CmpNewsletterRecipientSourceKindEnum.ActivePrimaryContacts,
  activeMembers: CmpNewsletterRecipientSourceKindEnum.ActiveMembers,
  allMembers: CmpNewsletterRecipientSourceKindEnum.AllMembers,
  nonMemberSubscribers:
    CmpNewsletterRecipientSourceKindEnum.NonMemberSubscribers,
  newMembers: CmpNewsletterRecipientSourceKindEnum.NewMembers,
  renewingMembers: CmpNewsletterRecipientSourceKindEnum.RenewingMembers,
  prospectMembers: CmpNewsletterRecipientSourceKindEnum.ProspectMembers,
  eventAttendees: CmpNewsletterRecipientSourceKindEnum.EventAttendees,
  pastEventAttendees: CmpNewsletterRecipientSourceKindEnum.PastEventAttendees,
  eblastSubscribers: CmpNewsletterRecipientSourceKindEnum.WebsiteSubscribers,
  directRecipients: CmpNewsletterRecipientSourceKindEnum.DirectEmails,
  selectedMembers: CmpNewsletterRecipientSourceKindEnum.SelectedMembers,
  governanceGroups: CmpNewsletterRecipientSourceKindEnum.GovernanceGroups,
  membersOfferingPerks:
    CmpNewsletterRecipientSourceKindEnum.MembersOfferingPerks,
  pendingMembers: CmpNewsletterRecipientSourceKindEnum.PendingMembers,
  suspendedMembers: CmpNewsletterRecipientSourceKindEnum.SuspendedMembers,
  eventSponsors: CmpNewsletterRecipientSourceKindEnum.EventSponsors,
};

const recipientSourceIdBySourceKind: Partial<
  Record<CmpNewsletterRecipientSourceKindEnum, RecipientSourceId>
> = {
  [CmpNewsletterRecipientSourceKindEnum.ActivePrimaryContacts]: "activePrimaryContacts",
  [CmpNewsletterRecipientSourceKindEnum.ActiveMembers]: "activeMembers",
  [CmpNewsletterRecipientSourceKindEnum.AllMembers]: "allMembers",
  [CmpNewsletterRecipientSourceKindEnum.NonMemberSubscribers]:
    "nonMemberSubscribers",
  [CmpNewsletterRecipientSourceKindEnum.NewMembers]: "newMembers",
  [CmpNewsletterRecipientSourceKindEnum.RenewingMembers]: "renewingMembers",
  [CmpNewsletterRecipientSourceKindEnum.ProspectMembers]: "prospectMembers",
  [CmpNewsletterRecipientSourceKindEnum.EventAttendees]: "eventAttendees",
  [CmpNewsletterRecipientSourceKindEnum.PastEventAttendees]:
    "pastEventAttendees",
  [CmpNewsletterRecipientSourceKindEnum.WebsiteSubscribers]:
    "eblastSubscribers",
  [CmpNewsletterRecipientSourceKindEnum.DirectEmails]: "directRecipients",
  [CmpNewsletterRecipientSourceKindEnum.SelectedMembers]: "selectedMembers",
  [CmpNewsletterRecipientSourceKindEnum.GovernanceGroups]: "governanceGroups",
  [CmpNewsletterRecipientSourceKindEnum.MembersOfferingPerks]:
    "membersOfferingPerks",
  [CmpNewsletterRecipientSourceKindEnum.PendingMembers]: "pendingMembers",
  [CmpNewsletterRecipientSourceKindEnum.SuspendedMembers]: "suspendedMembers",
  [CmpNewsletterRecipientSourceKindEnum.EventSponsors]: "eventSponsors",
};

export const formStateToRecipientSelectionInput = (
  email: EmailFormState
): CmpNewsletterRecipientSelectionInput => {
  const additionalRecipients = email.additionalRecipients.map((recipient) => ({
    email: recipient.email.trim().toLowerCase(),
    firstName: recipient.firstName.trim(),
    lastName: recipient.lastName.trim(),
    companyName: recipient.companyName?.trim() || undefined,
  }));
  const directEmails = Array.from(
    new Set([
      ...splitDirectRecipients(email.directRecipients).map((address) =>
        address.toLowerCase()
      ),
      ...additionalRecipients.map((recipient) => recipient.email),
    ])
  );
  const recipientSourceIds = [...email.recipientSourceIds];

  if (
    email.governanceGroupIds.length > 0 &&
    !recipientSourceIds.includes("governanceGroups")
  ) {
    recipientSourceIds.push("governanceGroups");
  }

  if (
    email.selectedMemberIds.length > 0 &&
    !recipientSourceIds.includes("selectedMembers")
  ) {
    recipientSourceIds.push("selectedMembers");
  }

  if (
    directEmails.length > 0 &&
    !recipientSourceIds.includes("directRecipients")
  ) {
    recipientSourceIds.push("directRecipients");
  }

  return {
    sourceKinds: Array.from(
      new Set(
        recipientSourceIds.map(
          (sourceId) => sourceKindByRecipientSourceId[sourceId]
        )
      )
    ),
    groupIds: email.recipientGroupIds,
    memberIds: email.selectedMemberIds,
    governanceGroupIds: email.governanceGroupIds,
    additionalRecipients,
    excludedEmails: Array.from(new Set(email.excludedRecipientEmails.map((address) => address.trim().toLowerCase()))),
    eventIds: email.eventIds,
    sponsorEventIds: email.sponsorEventIds,
    criteriaIds: email.criteriaIds,
    directEmails,
    memberFilters: {
      membershipTypeIds: email.memberFilters.membershipTypeIds,
      employeeRangeIds: email.memberFilters.employeeRangeIds,
      categoryIds: email.memberFilters.categoryIds,
      memberSinceRangeIds: email.memberFilters.memberSinceRangeIds,
      renewalRangeIds: email.memberFilters.renewalRangeIds,
    },
  };
};

const sortRecipientSelectionValues = <Value extends string>(
  values: Value[] | null | undefined
): Value[] => [...(values || [])].sort();

// Audience preview requests depend only on recipient selection. Canonicalizing
// every array prevents unrelated draft edits or selection ordering from
// restarting the debounced backend preview.
export const getEmailAudienceSelectionKey = (email: EmailFormState): string => {
  const selection = formStateToRecipientSelectionInput(email);
  const memberFilters = selection.memberFilters;

  return JSON.stringify({
    sourceKinds: sortRecipientSelectionValues(selection.sourceKinds),
    criteriaIds: sortRecipientSelectionValues(selection.criteriaIds),
    excludedEmails: sortRecipientSelectionValues(selection.excludedEmails),
    groupIds: sortRecipientSelectionValues(selection.groupIds),
    memberIds: sortRecipientSelectionValues(selection.memberIds),
    governanceGroupIds: sortRecipientSelectionValues(
      selection.governanceGroupIds
    ),
    additionalRecipients: [...(selection.additionalRecipients || [])].sort(
      (first, second) => first.email.localeCompare(second.email)
    ),
    eventIds: sortRecipientSelectionValues(selection.eventIds),
    sponsorEventIds: sortRecipientSelectionValues(selection.sponsorEventIds),
    directEmails: sortRecipientSelectionValues(
      selection.directEmails?.map((emailAddress) => emailAddress.toLowerCase())
    ),
    memberFilters: {
      membershipTypeIds: sortRecipientSelectionValues(
        memberFilters?.membershipTypeIds
      ),
      employeeRangeIds: sortRecipientSelectionValues(
        memberFilters?.employeeRangeIds
      ),
      categoryIds: sortRecipientSelectionValues(memberFilters?.categoryIds),
      memberSinceRangeIds: sortRecipientSelectionValues(
        memberFilters?.memberSinceRangeIds
      ),
      renewalRangeIds: sortRecipientSelectionValues(
        memberFilters?.renewalRangeIds
      ),
    },
  });
};

type AssertEmailBuilderDraftPersistedInput = {
  submittedEmail: Pick<EmailFormState, "builderState">;
  savedEmail: Pick<EmailFormState, "builderState">;
};

const sortPersistenceValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortPersistenceValue);
  if (!value || typeof value !== "object") return value;

  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((sortedValue, key) => {
      sortedValue[key] = sortPersistenceValue(
        (value as Record<string, unknown>)[key]
      );
      return sortedValue;
    }, {});
};

const getEmailBuilderPersistenceSignature = (
  email: Pick<EmailFormState, "builderState">
): string =>
  JSON.stringify(
    sortPersistenceValue(sanitizeEmailBuilderStateLinks(email.builderState))
  );

/**
 * A successful draft mutation must echo the exact builder state it accepted.
 * Failing closed here prevents navigation and a misleading success notice if
 * a stale client snapshot, API regression, or cache response drops blocks.
 */
export const assertEmailBuilderDraftPersisted = ({
  submittedEmail,
  savedEmail,
}: AssertEmailBuilderDraftPersistedInput): void => {
  if (
    getEmailBuilderPersistenceSignature(submittedEmail) !==
    getEmailBuilderPersistenceSignature(savedEmail)
  ) {
    throw new UserFacingError(
      "We could not confirm that all your email content was saved. Your changes are still on the screen. Select Save Draft or Update Draft again. If it keeps happening, email moeen@onechambersystem.com."
    );
  }
};

export const emailFormStateToInput = (
  email: EmailFormState,
  footerProfile?: EmailFooterProfile | null
): CmpNewsletterDataInput => {
  const scheduledAtUtcMilli = toUtcMilli(
    email.scheduledDate,
    email.scheduledTime,
    email.timeZone
  );
  if (hasInvalidEmailSchedule(email)) {
    throw new UserFacingError("The Scheduled Date or Scheduled Time is not valid. Choose them again, then save.");
  }
  const safeBuilderState = sanitizeEmailBuilderStateLinks(email.builderState);
  const builderContent = renderEmailBuilderState(
    safeBuilderState,
    footerProfile || undefined
  );
  // Body and footer are stored separately so the backend send wrapper
  // (hidden preheader + footer append) does not double the footer block.
  const contentHtml = builderContent.bodyHtml || textToHtml(email.content);
  const contentText = builderContent.text || email.content;

  return {
    id: email.id,
    subject: email.subject.trim() || "Untitled email",
    campaignName: email.campaignName.trim(),
    campaignDescription: email.campaignDescription.trim(),
    reviewNote: email.reviewNote.trim(),
    preheader: email.preheader.trim() || undefined,
    fromName: email.fromName.trim() || undefined,
    fromEmail: email.fromEmail.trim() || undefined,
    replyToEmail: email.replyToEmail.trim() || undefined,
    emailType: emailTypeToApi(email.emailType),
    status: statusToApi(email.status === "sent" ? "draft" : email.status),
    scheduledAtUtcMilli,
    templateKey: email.templateId || undefined,
    builderStateJson: serializeEmailBuilderState(safeBuilderState),
    contentHtml,
    contentText,
    footerHtml: builderContent.footerHtml || undefined,
    recipientSelection: formStateToRecipientSelectionInput(email),
    newsroom: {
      publishToNewsroom: email.publishToNewsroom,
      slug: email.newsroomSlug.trim() || undefined,
      title: email.seoTitle.trim() || email.subject.trim() || undefined,
      summary:
        email.seoDescription.trim() || email.preheader.trim() || undefined,
      body: contentHtml || undefined,
      featuredImageUrl: email.newsroomImageUrl.trim() || undefined,
      category: email.category.trim() || undefined,
      tags: splitCommaList(email.tags),
      authorName: email.fromName.trim() || undefined,
    },
    attachments: email.attachments.map((attachment) => ({
      id: attachment.id,
      fileName: attachment.name,
      mimeType: attachment.fileType || "application/octet-stream",
      sizeBytes: attachment.sizeBytes,
      storageKey: attachment.storageKey,
    })),
  };
};

// Template keys stored before the 8-template gallery (version-1 wizard) map
// to their nearest current template; anything unknown falls back to Custom
// Email so the saved canvas stays editable.
const legacyTemplateKeyMap: Record<string, EmailTemplateKey> = {
  chamberNewsletter: "newsletter",
  mediaRelease: "mediaRelease",
  advocacyUpdate: "advocacyUpdate",
  fromTheDesk: "fromTheDesk",
};

const templateIdFromStoredKey = (
  templateKey?: string | null
): EmailTemplateSelection => {
  // No stored key = the campaign never picked a template, so Step 3 must
  // land on the template GALLERY, not an empty builder canvas.
  if (!templateKey) {
    return "";
  }
  if (getEmailTemplatePreset(templateKey)) {
    return templateKey as EmailTemplateKey;
  }

  return legacyTemplateKeyMap[templateKey] || "customEmail";
};

export const newsletterReturnToFormState = (
  newsletter: CmpNewsletterFieldsFragment,
  timeZone = "America/Toronto"
): EmailFormState => {
  const scheduled = toLocalDateTimeInputParts(newsletter.scheduledAtUtcMilli, timeZone);
  const recipientSourceIds = newsletter.recipientSelection.sourceKinds
    .map((sourceKind) => recipientSourceIdBySourceKind[sourceKind])
    .filter(Boolean) as RecipientSourceId[];
  const content =
    newsletter.contentText?.trim() ||
    htmlToText(newsletter.contentHtml) ||
    htmlToText(newsletter.newsroom.body);
  const builderState = parseEmailBuilderStateJson({
    builderStateJson: newsletter.builderStateJson,
    chamberName: newsletter.fromName || undefined,
    fallbackContent: content,
    fallbackHtml: newsletter.contentHtml,
    fallbackFooterHtml: newsletter.footerHtml,
  });

  return {
    ...buildDefaultCreateEmailForm({
      chamberName: newsletter.fromName || undefined,
      senderEmail: newsletter.fromEmail,
      replyToEmail: newsletter.replyToEmail || undefined,
    }),
    id: newsletter.id,
    isTestData: !!newsletter.isCmpNewsletterModuleTestData,
    status: statusFromApi(newsletter.status),
    subject: newsletter.subject,
    preheader: newsletter.preheader || "",
    fromName: newsletter.fromName || "",
    fromEmail: newsletter.fromEmail,
    replyToEmail: newsletter.replyToEmail || "",
    emailType: emailTypeFromApi(newsletter.emailType),
    campaignName: newsletter.campaignName || "",
    campaignDescription: newsletter.campaignDescription || "",
    reviewNote: newsletter.reviewNote || "",
    reviewHistory: newsletter.reviewHistory || [],
    category: newsletter.newsroom.category || "",
    tags: newsletter.newsroom.tags.join(", "),
    recipientSourceIds,
    memberFilters: {
      membershipTypeIds:
        newsletter.recipientSelection.memberFilters.membershipTypeIds,
      employeeRangeIds:
        newsletter.recipientSelection.memberFilters.employeeRangeIds,
      categoryIds: newsletter.recipientSelection.memberFilters.categoryIds,
      memberSinceRangeIds:
        newsletter.recipientSelection.memberFilters.memberSinceRangeIds,
      renewalRangeIds:
        newsletter.recipientSelection.memberFilters.renewalRangeIds,
    },
    recipientGroupIds: newsletter.recipientSelection.groupIds,
    selectedMemberIds: newsletter.recipientSelection.memberIds,
    eventIds: newsletter.recipientSelection.eventIds,
    sponsorEventIds: newsletter.recipientSelection.sponsorEventIds || [],
    criteriaIds: newsletter.recipientSelection.criteriaIds || [],
    governanceGroupIds: newsletter.recipientSelection.governanceGroupIds || [],
    additionalRecipients: (
      newsletter.recipientSelection.additionalRecipients || []
    ).map((recipient) => ({
      email: recipient.email,
      firstName: recipient.firstName,
      lastName: recipient.lastName,
      companyName: recipient.companyName,
    })),
    excludedRecipientEmails: newsletter.recipientSelection.excludedEmails || [],
    directRecipients: newsletter.recipientSelection.directEmails.join(", "),
    templateId: templateIdFromStoredKey(newsletter.templateKey),
    content,
    builderState,
    attachments: newsletter.attachments.map((attachment) => ({
      id: attachment.id,
      name: attachment.fileName,
      fileType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      sizeLabel: formatFileSize(attachment.sizeBytes),
      storageKey: attachment.storageKey || undefined,
      eTag: attachment.eTag,
    })),
    publishToNewsroom: newsletter.newsroom.publishToNewsroom,
    newsroomStatus: newsroomStatusFromApi(newsletter.newsroom.status),
    newsroomSlug: newsletter.newsroom.slug || "",
    newsroomImageUrl: newsletter.newsroom.featuredImageUrl || "",
    newsroomPublishedAtUtcMilli: newsletter.newsroom.publishedAtUtcMilli || null,
    newsroomUnpublishedAtUtcMilli: newsletter.newsroom.unpublishedAtUtcMilli || null,
    seoTitle: newsletter.newsroom.title || newsletter.subject,
    seoDescription: newsletter.newsroom.summary || newsletter.preheader || "",
    timeZone,
    scheduledDate: scheduled.date,
    scheduledTime: scheduled.time || "10:00",
    estimatedRecipientCount: newsletter.estimatedRecipientCount,
    invalidRecipientCount: newsletter.invalidRecipientCount,
    skippedRecipientCount: newsletter.skippedRecipientCount,
    sentRecipientCount: newsletter.sentRecipientCount,
    failedRecipientCount: newsletter.failedRecipientCount,
    skippedDeliveryCount: newsletter.skippedDeliveryCount,
    openCount: newsletter.openCount,
    clickCount: newsletter.clickCount,
    outboundEmails: (newsletter.outboundEmails || []).map((outboundEmail) =>
      outboundEmailReturnToUi(outboundEmail, timeZone)
    ),
    deliverySummary: deliverySummaryReturnToUi(newsletter, timeZone),
    senderReadinessLabel:
      newsletter.senderReadiness.status ===
      CmpNewsletterSenderReadinessStatusEnum.Verified
        ? "Verified"
        : newsletter.senderReadiness.status ===
            CmpNewsletterSenderReadinessStatusEnum.FallbackAvailable
          ? "Fallback available"
          : "Blocked",
    senderReadinessDetail:
      newsletter.senderReadiness.blockingReason ||
      newsletter.senderReadiness.warning ||
      "We checked this sender email.",
  };
};

export const recipientGroupReturnToUiGroup = (
  group: CmpNewsletterRecipientGroupFieldsFragment
): RecipientGroup => ({
  id: group.id,
  name: group.name,
  description: group.description || "Staff-created recipient group.",
  count: group.estimatedRecipientCount,
  source:
    group.sourceKinds.length > 0
      ? group.sourceKinds
          .map((sourceKind) => sourceKind.replace(/_/g, " ").toLowerCase())
          .join(", ")
      : "Manual group",
  isArchived: group.isArchived,
  selection: {
    name: group.name,
    description: group.description,
    sourceKinds: [...group.sourceKinds],
    criteriaIds: [...(group.criteriaIds || [])],
    governanceGroupIds: [...group.governanceGroupIds],
    memberIds: [...group.memberIds],
    eventIds: [...group.eventIds],
    directEmails: [...group.directEmails],
    additionalRecipients: group.additionalRecipients.map(({ email, firstName, lastName, companyName }) => ({ email, firstName, lastName, companyName })),
  },
});

export const manageNewsletterRowToEmailCampaign = (
  row: ManageNewsletterRow,
  timeZone?: string | null
): EmailCampaign => ({
  id: row.id,
  slug: row.slug,
  subject: row.subject,
  emailType: emailTypeFromApi(row.emailType),
  category: row.campaignName || "",
  audienceLabel: row.audienceLabel || "",
  tags: row.tags || [],
  status: statusFromApi(row.status),
  newsroomStatus: newsroomStatusFromApi(row.newsroomStatus),
  scheduledAt: formatDateTime(row.scheduledAtUtcMilli, timeZone),
  sentAt: formatDateTime(row.sentAtUtcMilli, timeZone),
  publishedAt: formatDate(row.publishedAtUtcMilli, timeZone),
  intendedRecipients: row.estimatedRecipientCount,
  invalidRecipients: row.invalidRecipientCount,
  skippedCandidateRecipients: row.skippedRecipientCount,
  sentRecipients: row.sentRecipientCount,
  skippedRecipients: row.skippedRecipientCount,
  failedRecipients: row.failedRecipientCount,
  skippedDeliveries: row.skippedDeliveryCount,
  openCount: row.openCount,
  clickCount: row.clickCount,
  attachmentCount: row.attachmentCount,
  updatedAt: formatDate(row.updatedAtUtcMilli, timeZone) || "Not saved yet",
});

export const previewQueryToSummary = (
  preview?: PreviewCmpNewsletterRecipientsQuery["previewCmpNewsletterRecipients"]
): RecipientPreviewSummary | null => {
  if (!preview) return null;

  return {
    recipients: preview.recipients,
    totalCandidateCount: preview.totalCandidateCount,
    uniqueValidRecipientCount: preview.uniqueValidRecipientCount,
    invalidRecipientCount: preview.invalidRecipientCount,
    skippedRecipientCount: preview.skippedRecipientCount,
    recipientOverrideUsed: preview.recipientOverrideUsed,
    actualTestRecipientEmail: preview.actualTestRecipientEmail,
    recipientRoutingMode: preview.recipientRoutingMode,
    sandboxNotice: preview.sandboxNotice ?? null,
  };
};

/**
 * Monday 13390105883 (S12): a Custom Audience List View counts every valid
 * contact of the list, with the ones a sandbox chamber's send skips
 * (skippedBySandbox), so it matches the CONTACTS column. The send count
 * stays uniqueValidRecipientCount.
 */
export const getCustomListContactCount = (preview: RecipientPreviewSummary) =>
  preview.uniqueValidRecipientCount +
  (preview.recipients || []).filter((row) => row.skippedBySandbox).length;
